const FILTERED_VALUE = '[Filtered]'

const SENSITIVE_FIELD_NAMES = new Set([
  'authorization',
  'proxyauthorization',
  'cookie',
  'setcookie',
  'sessioncookie',
  'bearer',
  'token',
  'idtoken',
  'accesstoken',
  'refreshtoken',
  'apikey',
])

// These shared /g patterns are stateful; use them only with replace(), not test() or exec().
const BEARER_CREDENTIAL_PATTERN = /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi
const JWT_PATTERN = /eyJ[A-Za-z0-9_-]*\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g
const SENSITIVE_JSON_FIELD_PATTERN =
  /("(?:authorization|proxy[-_]?authorization|cookie|set[-_]?cookie|session[-_]?cookie|bearer|token|id[-_]?token|access[-_]?token|refresh[-_]?token|api[-_]?key)"\s*:\s*)"[^"]*"/gi
const SENSITIVE_QUERY_PARAMETER_PATTERN = /(^|[?&])((?:id[-_]?token|access[-_]?token|refresh[-_]?token|api[-_]?key)=)[^&#\s]*/gi
// Signed storage URLs (GCS V4/V2, S3, Azure SAS) carry a short-lived download credential in the
// query string. The signature by itself grants anyone who reads it access to the object until it
// expires, so it must never reach Sentry.
const SIGNED_URL_CREDENTIAL_PARAMETER_PATTERN =
  /(^|[?&])((?:x-goog-signature|x-goog-credential|x-amz-signature|x-amz-credential|x-amz-security-token|googleaccessid|signature|sig)=)[^&#\s]*/gi

function isSensitiveFieldName(fieldName: string): boolean {
  const normalizedFieldName = fieldName.toLowerCase().replace(/[^a-z0-9]/g, '')
  return SENSITIVE_FIELD_NAMES.has(normalizedFieldName)
}

function redactSensitiveStrings(value: string): string {
  return value
    .replace(SENSITIVE_JSON_FIELD_PATTERN, `$1"${FILTERED_VALUE}"`)
    .replace(SENSITIVE_QUERY_PARAMETER_PATTERN, (_match, delimiter: string, parameter: string) => `${delimiter}${parameter}${FILTERED_VALUE}`)
    .replace(SIGNED_URL_CREDENTIAL_PARAMETER_PATTERN, (_match, delimiter: string, parameter: string) => `${delimiter}${parameter}${FILTERED_VALUE}`)
    .replace(BEARER_CREDENTIAL_PATTERN, 'Bearer [Filtered]')
    .replace(JWT_PATTERN, FILTERED_VALUE)
}

function isPlainObject(value: object): boolean {
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

function redactValue(value: unknown, copies: WeakMap<object, unknown>): unknown {
  if (typeof value === 'string') {
    return redactSensitiveStrings(value)
  }

  if (typeof value !== 'object' || value === null) {
    return value
  }

  if (copies.has(value)) {
    return copies.get(value)
  }

  if (Array.isArray(value)) {
    const redactedArray: unknown[] = []
    copies.set(value, redactedArray)
    value.forEach((item) => {
      redactedArray.push(redactValue(item, copies))
    })
    return redactedArray
  }

  if (!isPlainObject(value)) {
    return value
  }

  const redactedRecord: Record<string, unknown> = {}
  copies.set(value, redactedRecord)
  Object.entries(value).forEach(([fieldName, fieldValue]) => {
    redactedRecord[fieldName] = isSensitiveFieldName(fieldName) ? FILTERED_VALUE : redactValue(fieldValue, copies)
  })
  return redactedRecord
}

// Sentry hands us frozen objects in some code paths, so never assign to a read-only field.
function writeRedactedField(target: object, fieldName: string, redactedValue: string): void {
  const descriptor = Object.getOwnPropertyDescriptor(target, fieldName)
  if (descriptor && descriptor.writable === false && descriptor.set === undefined) {
    return
  }
  ;(target as Record<string, unknown>)[fieldName] = redactedValue
}

function redactStringEntriesInPlace(record: unknown): void {
  if (typeof record !== 'object' || record === null) {
    return
  }

  Object.entries(record).forEach(([fieldName, fieldValue]) => {
    if (typeof fieldValue !== 'string') {
      return
    }
    writeRedactedField(record, fieldName, isSensitiveFieldName(fieldName) ? FILTERED_VALUE : redactSensitiveStrings(fieldValue))
  })
}

/**
 * Transaction events carry Sentry `Span` class instances rather than plain objects, so the copying
 * redaction below skips them. Outgoing HTTP spans keep the full request URL in the span description
 * and in the span data/attributes, which is where signed storage URLs leak. Those fields are
 * redacted on the span itself because a copied span would no longer serialize correctly.
 */
function redactSpanFieldsInPlace(event: unknown): void {
  const spans = (event as { spans?: unknown } | null | undefined)?.spans
  if (!Array.isArray(spans)) {
    return
  }

  spans.forEach((span: unknown) => {
    if (typeof span !== 'object' || span === null) {
      return
    }

    const spanFields = span as { description?: unknown; data?: unknown; _attributes?: unknown }
    if (typeof spanFields.description === 'string') {
      writeRedactedField(span, 'description', redactSensitiveStrings(spanFields.description))
    }
    redactStringEntriesInPlace(spanFields.data)
    redactStringEntriesInPlace(spanFields._attributes)
  })
}

/** Remove credentials from an error or transaction immediately before Sentry sends it. */
export function redactSensitiveSentryEventData<T>(event: T): T {
  redactSpanFieldsInPlace(event)
  return redactValue(event, new WeakMap<object, unknown>()) as T
}
